import {
    Component,
    Element,
    Event,
    EventEmitter,
    Host,
    h,
    Prop,
    Watch,
} from "@stencil/core";

/**
 * @slot image - Slot for the image (`<picture>` or `<img>`)
 * @slot title - Slot for the headline (`<h2>` or `<h3>`)
 * @slot (default) - Slot for the text content
 */
@Component({
    tag: "ux-iws-nb-all-in-one-tile",
    styleUrl: "nb-all-in-one-tile.scss",
    shadow: false,
})
export class NbAllInOneTile {
    private static uidIncrement = 0;
    private tileIndex: number = 0;
    private uid = `nb-all-in-one-tile-${NbAllInOneTile.uidIncrement++}`;

    /** The element reference for the component. */
    @Element() host: HTMLUxIwsNbAllInOneTileElement;

    /** The related group identifier */
    @Prop({ reflect: true }) group: string = "";

    /** Fallback label if no title slot is provided */
    @Prop() label: string = "";

    /** Label for the CTA button if the tile is inactive */
    @Prop() activateLabel: string = "Show details";

    /** Label for the CTA button if the tile is active */
    @Prop() deactivateLabel: string = "Hide details";

    /** Indicates if the tile is active or not */
    @Prop({ mutable: true, reflect: true }) active: boolean = false;

    private getAllTiles = (): HTMLUxIwsNbAllInOneTileElement[] => {
        return Array.from(
            this.host.parentElement.querySelectorAll<HTMLUxIwsNbAllInOneTileElement>(
                "ux-iws-nb-all-in-one-tile"
            )
        );
    };

    /** Event that is emitted when the show details button is clicked */
    @Event({
        eventName: "uxShow",
        cancelable: true,
        composed: true,
        bubbles: true,
    })
    showEvent: EventEmitter<string>;

    /** Event that is emitted when the hide details button is clicked */
    @Event({
        eventName: "uxHide",
        cancelable: false,
        composed: true,
        bubbles: true,
    })
    hideEvent: EventEmitter<string>;

    @Watch("active")
    handleActiveChange(active: boolean) {
        if (!active) {
            return;
        }
        this.getAllTiles().forEach((el) => (el.active = el === this.host));
    }

    componentWillLoad() {
        this.tileIndex = this.getAllTiles().indexOf(this.host);
    }

    private handleClick = () => {
        if (this.active) {
            this.hideEvent.emit(this.group);
            this.active = false;
            return;
        }
        if (this.showEvent.emit(this.group).defaultPrevented) {
            return;
        }
        this.active = true;
    };

    private hasActiveTile = (): boolean => {
        return this.getAllTiles().some((el) => el.active);
    };

    render() {
        const baseClass = "ux-iws-nb-all-in-one-tile";
        const focusable = this.hasActiveTile()
            ? this.active
            : this.tileIndex === 0;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--active`]: this.active,
                }}
                role="button"
                aria-labelledby={`${this.uid}-title`}
                aria-describedby={`${this.uid}-desc`}
                aria-haspopup="true"
                onClick={this.handleClick}
                tabindex={focusable ? 0 : -1}
            >
                <div class={`${baseClass}__image`} role="presentation">
                    <slot name="image" />
                </div>
                <div class={`${baseClass}__gradient`} role="presentation" />
                <div class={`${baseClass}__content`}>
                    <div class={`${baseClass}__title`} id={`${this.uid}-title`}>
                        <slot name="title">{this.label}</slot>
                    </div>
                    <div class={`${baseClass}__text`} id={`${this.uid}-desc`}>
                        <slot />
                    </div>
                    <div class={`${baseClass}__cta`}>
                        <span class={`${baseClass}__cta-button`}>
                            <span class="ux-label">
                                {this.active
                                    ? this.deactivateLabel
                                    : this.activateLabel}
                            </span>
                            <ux-icon
                                name="plus"
                                weight="bold"
                                aria-hidden="true"
                            />
                        </span>
                    </div>
                </div>
            </Host>
        );
    }
}
